import React, { useState } from 'react';
import { ChevronDown, HelpCircle, MessageSquare, ShieldCheck, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQs: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    { 
      q: "How is my credit score calculated?", 
      a: "We use a proprietary character-based algorithm that looks at your business cashflow, stewardship of past tranches, and agent-verified data. Unlike traditional banks, we value your real-world business performance over static credit bureau reports.",
      icon: HelpCircle 
    },
    { 
      q: "How can I improve my score to reach the next tier?", 
      a: "Consistency is key. Maintaining a 90% on-time repayment status across your daily tranches is the primary trigger for unlocking higher capital limits and lower subscription fees. Your growth pathway is deterministic: perform well, and you scale automatically.",
      icon: Zap
    },
    { 
      q: "What happens if I miss a payment?", 
      a: "We believe in fair treatment. If you face a temporary setback, contact your designated Guardian immediately. We offer rescheduling options and do not use punitive CRB destruction for honest businesses working through challenges.",
      icon: ShieldCheck
    },
    { 
      q: "How is pricing calculated?", 
      a: "We use an 'Effective Interest Rate' based on total scheduled charges annualized over a 360-day basis. We do not charge hidden processing fees or 'facility' fees. Everything is disclosed upfront in your Member Portal.",
      icon: MessageSquare
    },
    { 
      q: "Can I apply for a loan top-up?", 
      a: "Yes. Once you have successfully repaid 50% of your current cycle with perfect stewardship, you may be eligible for a growth top-up. This is subject to an updated cashflow assessment by your agent.",
      icon: HelpCircle
    },
    { 
      q: "Who is a Guardian?", 
      a: "A Guardian is your dedicated relationship manager. They are local experts who verify your business and provide coaching to help you move through the growth tiers. They are your primary point of contact for all support needs.",
      icon: HelpCircle
    }
  ];

  return (
    <div className="bg-[#f8fafc] min-h-screen pb-32">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 text-center">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-chui-navy/5 text-chui-navy text-[10px] font-black uppercase tracking-[0.25em] mb-8 border border-chui-navy/10">
          <HelpCircle size={14} className="mr-2" /> Knowledge Base
        </div>
        <h1 className="text-5xl md:text-7xl font-black text-chui-navy mb-8 tracking-tighter uppercase">
          Frequently Asked
        </h1>
        <p className="text-xl text-slate-700 max-w-2xl mx-auto font-bold leading-relaxed">
          Everything you need to know about the Chui Credit ecosystem and your path to business growth.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-sm transition-all hover:shadow-xl group">
              <button 
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex justify-between items-center p-8 lg:p-10 text-left font-black text-slate-900 text-lg tracking-tight uppercase"
              >
                <span className="flex items-center">
                   <div className={`w-10 h-10 rounded-xl mr-6 flex items-center justify-center transition-colors ${openFaq === i ? 'bg-chui-navy text-chui-yellow' : 'bg-slate-50 text-slate-400 group-hover:text-chui-navy'}`}>
                      <faq.icon size={20} />
                   </div>
                   {faq.q}
                </span>
                <div className={`p-3 rounded-2xl transition-all duration-500 ${openFaq === i ? 'bg-chui-navy text-white rotate-180' : 'bg-slate-100 text-slate-500'}`}>
                  <ChevronDown size={20} strokeWidth={3} />
                </div>
              </button>
              <div className={`transition-all duration-500 ease-in-out overflow-hidden ${openFaq === i ? 'max-h-96 opacity-100 border-t border-slate-100' : 'max-h-0 opacity-0'}`}>
                <div className="p-10 text-lg text-slate-800 leading-relaxed font-bold">
                  {faq.a}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Support CTA */}
        <div className="mt-20 bg-chui-navy rounded-[3rem] p-12 text-white text-center relative overflow-hidden shadow-2xl">
           <div className="absolute inset-0 opacity-10 pointer-events-none">
              <HelpCircle size={300} className="absolute -top-20 -right-20" />
           </div>
           <h3 className="text-3xl font-black mb-6 uppercase tracking-tight relative z-10">Still have questions?</h3>
           <p className="text-white/70 font-bold mb-10 text-lg relative z-10">Our Guardians are ready to help you navigate your growth journey.</p>
           <div className="flex flex-wrap justify-center gap-6 relative z-10">
              <Link to="/apply" className="bg-white text-chui-navy px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-chui-yellow transition-all shadow-xl">Start Application</Link>
              <button className="bg-white/10 text-white border border-white/20 px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-white/20 transition-all">Contact Support</button>
           </div>
        </div>
      </div>
    </div>
  );
};

export default FAQs;